/**
 * Custom hook for managing authentication state
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useRouter } from 'next/router';
import { AuthTokens } from '../types/auth';
import { getStoredTokens } from '../utils/storage';
import { isTokenExpired } from '../utils/tokenUtils';
import { UserProfileData } from '../types/profile';

export interface AuthState {
  user: AuthTokens | null;
  profileDetails: UserProfileData | null;
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  needsOnboarding: boolean;
}

export interface AuthStateActions {
  setUser: (user: AuthTokens | null) => void;
  setProfileDetails: (profileDetails: UserProfileData | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setAuthenticated: (authenticated: boolean) => void;
  setNeedsOnboarding: (needsOnboarding: boolean) => void;
  clearAuth: () => void;
}

export const useAuthState = (): [AuthState, AuthStateActions] => {
  const router = useRouter();
  const [state, setState] = useState<AuthState>({
    user: null,
    profileDetails: null,
    isLoading: true,
    error: null,
    isAuthenticated: false,
    needsOnboarding: false,
  });

  const setUser = useCallback((user: AuthTokens | null) => {
    setState(prev => ({ ...prev, user }));
  }, []);

  const setProfileDetails = useCallback((profileDetails: UserProfileData | null) => {
    setState(prev => ({ ...prev, profileDetails }));
  }, []);

  const setLoading = useCallback((isLoading: boolean) => {
    setState(prev => ({ ...prev, isLoading }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState(prev => ({ ...prev, error }));
  }, []);

  const setAuthenticated = useCallback((isAuthenticated: boolean) => {
    setState(prev => ({ ...prev, isAuthenticated }));
  }, []);

  const setNeedsOnboarding = useCallback((needsOnboarding: boolean) => {
    setState(prev => ({ ...prev, needsOnboarding }));
  }, []);

  const clearAuth = useCallback(() => {
    setState({
      user: null,
      profileDetails: null,
      isLoading: false,
      error: null,
      isAuthenticated: false,
      needsOnboarding: false,
    });
  }, []);

  const actions: AuthStateActions = useMemo(() => ({
    setUser,
    setProfileDetails,
    setLoading,
    setError,
    setAuthenticated,
    setNeedsOnboarding,
    clearAuth,
  }), [setUser, setProfileDetails, setLoading, setError, setAuthenticated, setNeedsOnboarding, clearAuth]);

  // Initialize auth state on mount only (not when user changes)
  useEffect(() => {
    // Skip if already initialized (user exists) - prevents race condition with verifyOtp
    if (state.user) {
      return;
    }

    const initializeAuth = async () => {
      try {
        const storedTokens = getStoredTokens();

        if (!storedTokens) {
          setAuthenticated(false);
          setLoading(false);
          return;
        }

        // Check if access token is expired
        if (isTokenExpired(storedTokens.access_token)) {
          // Don't clear auth yet, let refresh service handle it
        }

        // Set all auth state together to prevent race conditions
        // Use a single state update to ensure atomicity
        setState(prev => ({
          ...prev,
          user: storedTokens,
          isAuthenticated: true,
          needsOnboarding: storedTokens.is_new_user || false,
          isLoading: false,
        }));

      } catch (error) {
        setError('Failed to initialize authentication');
        setLoading(false);
      }
    };

    initializeAuth().catch(error => {
      setError('Failed to initialize authentication');
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - callbacks are stable

  return [state, actions];
};
